import type { OrganizationTheme } from '../api/types'
import {
  allThemeProperties,
  COLOR_TOKENS,
  FONT_PROPERTIES,
  RADIUS_TOKENS,
  SPACING_TOKENS,
  TOKEN_PREFIX,
} from './tokens'

/*
  Applies an organization's theme to the live document.

  WHY THE DOM API AND NOT AN INJECTED <style>

  Every value here was typed by a customer's administrator, and the two ways to get a
  custom property onto the page are not equally safe. Building a stylesheet string and
  injecting it means a value containing `; }` closes the rule and opens a new one, and
  the theme becomes stored CSS injection against every user in the organization.
  `setProperty` cannot do that: the browser parses the value against the property's
  grammar and drops it if it does not fit. Nothing escapes the declaration because
  nothing is ever concatenated into one.

  The API validates on the way in as well. Neither check is redundant: the API's is what
  gives the admin a refusal they can act on, and this one is what holds if a document
  ever reaches the client by some other route.

  WHY EVERY CHANGE IS A FULL REVERT AND REAPPLY

  Switching organizations is the normal case, not the edge case — the switcher is in the
  header. If applying a theme only ever set properties, an organization that overrides
  twelve colours followed by one that overrides three would leave nine of the first
  organization's colours on the page. So the exact set of properties this module wrote
  is remembered, and removed before the next set is written.
*/

/** The custom properties this module has written, so it can take them all back. */
let appliedProperties: string[] = []

/** The last theme applied, so it can be re-asserted. See {@link reapplyTheme}. */
let lastApplied: OrganizationTheme | null = null

/**
 * The favicon href the page shipped with, captured before anything overwrites it.
 *
 * The title needs no equivalent: `resolveBrand` falls back to the same product name the
 * document was served with, so clearing a theme restores it without anything remembered.
 * The icon is a data URI in `index.html` and would have to be duplicated here to be
 * reconstructed, so it is captured instead.
 */
let defaultIconHref: string | null = null

/**
 * The values the stylesheet declares, captured before any theme is written.
 *
 * WHY IT HAS TO BE CAPTURED AND CANNOT BE READ ON DEMAND
 *
 * Custom properties inherit. The theme is written onto `documentElement`, so by the time
 * a branding editor asks "what is the default primary?", every element in the document —
 * including any probe it creates — has inherited the ORGANIZATION's primary. There is no
 * element left in the tree that still sees the stylesheet's value. Reading it once,
 * before the first theme is applied, is the only reliable way to keep it.
 *
 * `applyTheme` is where that moment is: it runs on mount with no theme yet, and it is
 * the only thing that ever writes these properties.
 */
let defaultTokens: Record<string, string> | null = null

const FONT_LINK_ID = 'tracer-theme-fonts'

export interface ResolvedBrand {
  /** What the product calls itself here. */
  productName: string
  /** The line under the stacked lockup. */
  tagline: string
  /** A customer mark to render in an <img>, or null to draw the product's own. */
  markUrl: string | null
  /** Whether to set the wordmark beside the mark. */
  showWordmark: boolean
}

export const DEFAULT_BRAND: ResolvedBrand = {
  productName: 'Tracer',
  tagline: 'Cost Allocation',
  markUrl: null,
  showWordmark: true,
}

/**
 * Fills in what the theme leaves out.
 *
 * A theme is an overlay, so a document that sets only a logo still has to produce a
 * complete brand — otherwise a screen would have to handle "themed but with no name",
 * which is not a state anything should have to render.
 */
export function resolveBrand(theme: OrganizationTheme | null | undefined): ResolvedBrand {
  if (!theme) {
    return DEFAULT_BRAND
  }

  const markUrl = theme.logo?.markUrl?.trim() || null

  return {
    productName: theme.productName?.trim() || DEFAULT_BRAND.productName,
    tagline: theme.tagline?.trim() || DEFAULT_BRAND.tagline,
    markUrl,
    /*
        A customer whose mark already contains their name gets the mark alone. The
        default is to show it, because a mark that is only a symbol needs the word — and
        an organization that has set no logo at all is still showing the Tracer mark,
        which has never been used without it.
    */
    showWordmark: markUrl === null ? true : (theme.logo?.showWordmark ?? true),
  }
}

/**
 * Writes a theme onto the document, replacing whatever the last call wrote.
 *
 * Safe to call with null, which is how an organization goes back to Tracer's own brand
 * and how the very first render behaves before the settings have loaded.
 */
export function applyTheme(theme: OrganizationTheme | null | undefined): void {
  const root = document.documentElement

  lastApplied = theme ?? null

  if (defaultIconHref === null) {
    defaultIconHref = findIconLink()?.getAttribute('href') ?? ''
  }

  /*
      Before the first write, and only then. Nothing in this module has touched
      documentElement's inline style yet, so what the computed style reports here is what
      the stylesheet says — which is the last moment that is true.
  */
  if (defaultTokens === null) {
    const computed = getComputedStyle(root)
    const captured: Record<string, string> = {}

    for (const property of allThemeProperties()) {
      captured[property] = computed.getPropertyValue(property).trim()
    }

    defaultTokens = captured
  }

  for (const property of appliedProperties) {
    root.style.removeProperty(property)
  }
  appliedProperties = []

  if (theme) {
    write(theme.colors, TOKEN_PREFIX.colors, COLOR_TOKENS)
    write(theme.radii, TOKEN_PREFIX.radii, RADIUS_TOKENS)
    write(theme.spacing, TOKEN_PREFIX.spacing, SPACING_TOKENS)

    for (const [key, property] of Object.entries(FONT_PROPERTIES)) {
      const stack = theme.fonts?.[key as keyof typeof FONT_PROPERTIES]
      if (typeof stack === 'string' && stack.trim()) {
        set(property, stack.trim())
      }
    }
  }

  applyFontStylesheet(theme?.fonts?.stylesheetUrl ?? null)
  applyIdentity(resolveBrand(theme), theme?.logo?.iconUrl ?? null)

  for (const listener of listeners) {
    listener()
  }

  function write(
    values: Record<string, string> | null | undefined,
    prefix: string,
    allowed: readonly string[],
  ) {
    if (!values) return

    for (const [token, value] of Object.entries(values)) {
      /*
          The allow-list is enforced again here, on the client.

          Not because the API's check is doubted, but because this is the last point
          before a name from a JSON document becomes a property name on the live
          document. A token the front end does not know is a token no stylesheet reads,
          so writing it would achieve nothing except leaving a property behind that the
          revert list is the only record of.
      */
      if (!allowed.includes(token)) continue
      if (typeof value !== 'string' || !value.trim()) continue

      set(`${prefix}${token}`, value.trim())
    }
  }

  function set(property: string, value: string) {
    root.style.setProperty(property, value)
    appliedProperties.push(property)
  }
}

/**
 * Loads the theme's font stylesheet, or removes the one a previous theme loaded.
 *
 * The link is replaced rather than mutated when the URL changes: setting `href` on a
 * live stylesheet link leaves the old sheet applied until the new one arrives, so an
 * organization switch would briefly render in the previous organization's faces.
 */
function applyFontStylesheet(url: string | null): void {
  const existing = document.getElementById(FONT_LINK_ID)

  if (!url) {
    existing?.remove()
    return
  }

  if (existing instanceof HTMLLinkElement && existing.href === url) {
    return
  }

  existing?.remove()

  const link = document.createElement('link')
  link.id = FONT_LINK_ID
  link.rel = 'stylesheet'
  /*
      No credentials to a third-party host, and no referrer beyond the origin. A font
      CDN has no business learning which screen of a cost-allocation system a hospital
      finance team is looking at.
  */
  link.crossOrigin = 'anonymous'
  link.referrerPolicy = 'strict-origin'
  link.href = url

  document.head.appendChild(link)
}

/** The browser tab: the product's name, and its mark. */
function applyIdentity(brand: ResolvedBrand, iconUrl: string | null): void {
  document.title = brand.productName

  const link = findIconLink() ?? createIconLink()

  // The mark falls back to the wide logo, which is usually wrong at 16px — and still
  // better than showing another company's favicon on this company's product.
  const href = iconUrl?.trim() || brand.markUrl || defaultIconHref || ''

  if (href) {
    link.setAttribute('href', href)
  }
}

function findIconLink(): HTMLLinkElement | null {
  return document.querySelector<HTMLLinkElement>('link[rel~="icon"]')
}

function createIconLink(): HTMLLinkElement {
  const link = document.createElement('link')
  link.rel = 'icon'
  document.head.appendChild(link)
  return link
}

/**
 * Re-asserts the saved theme after something else has written over these properties.
 *
 * There is exactly one caller and it needs to exist: the branding editor paints the draft
 * palette onto the live document as it is typed, and takes it off again when the admin
 * leaves. Both layers write to `documentElement`'s inline style — the SAME declaration —
 * so the editor's `removeProperty` strips the value whoever set it. Without this, leaving
 * the branding screen would clear the organization's saved colours and leave them cleared,
 * because the theme object has not changed and the provider's effect will not re-run.
 *
 * Cheap: it re-runs the same writes with the same values, so the repaint is a no-op.
 */
export function reapplyTheme(): void {
  applyTheme(lastApplied)
}

/**
 * Told after every theme change.
 *
 * For the handful of things that cannot read a custom property and need a resolved
 * colour handed to them — Monaco, which takes its theme as literal hex, and anything
 * else that paints to a canvas. Everything built from CSS restyles on its own and must
 * not subscribe.
 *
 * A subscription rather than a call from the provider, because the provider mounts above
 * every screen and importing Monaco into it would pull three and a half megabytes out of
 * its own chunk and into the entry bundle.
 */
const listeners = new Set<() => void>()

/** Subscribes; returns the unsubscribe, so an effect can return it directly. */
export function onThemeApplied(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * A token's current value as `#rrggbb`, for a consumer that cannot take `var()`.
 *
 * Expands the three-digit form and drops any alpha, because the callers here are APIs
 * that accept six digits and silently ignore anything else. `fallback` covers a token
 * that is somehow absent — a themed component rendering in the wrong colour is a bug; one
 * rendering in no colour at all is an invisible screen.
 */
export function resolveColor(token: string, fallback: string): string {
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(`${TOKEN_PREFIX.colors}${token}`)
    .trim()

  if (/^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(raw)) return raw.slice(0, 7)

  if (/^#[0-9a-fA-F]{3}$/.test(raw)) {
    return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`
  }

  return fallback
}

/**
 * What the stylesheet declares for each token, for the branding editor's "default"
 * swatch and its reset control.
 *
 * Empty until the first {@link applyTheme}, which is where the values are captured — see
 * {@link defaultTokens}. In practice the theme provider mounts above every screen, so
 * any caller has one.
 */
export function getDefaultTokens(): Record<string, string> {
  return defaultTokens ?? {}
}

/** Test seam: forget what the document shipped with. */
export function resetThemeForTests(): void {
  appliedProperties = []
  defaultIconHref = null
  defaultTokens = null
  lastApplied = null
}
